import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Image, ImageDayCount, MappedImage } from '@/domain/images';
import { withTimezoneMetadata } from '@/domain/images';
import { toDateString } from '@/utils/date';
import type { ParsedQuery } from '@/utils/queryParams';

import useQueryParams from './useQueryParams';

export type { ImageDayCount };

interface ImageInput<T> {
    data?: T[] | unknown;
    filter?: (image: Image) => boolean;
    /** Returning null drops the row, which is how the laksvel mapper skips a zero score. */
    mapper: (data: T) => MappedImage | null;
    loading: boolean;
    queryParam?: string;
    timezone?: string;
    dayswithimages?: ImageDayCount[];
    onDayChange?: (day: string) => void;
    isFetching?: boolean;
}

function queryParamToImageId(
    query: ParsedQuery<string>,
    queryParam: string | undefined
): string | undefined {
    if (queryParam === undefined) return undefined;
    const entry = query[queryParam];
    if (typeof entry === 'string') {
        return entry;
    }
    return undefined;
}

export interface ImageState {
    images: Image[];
    dayswithimages: ImageDayCount[];
    onAfterTimelineChangeHandler: (value: string) => void;
    initialImageIndex: number;
    loading: boolean;
    selectedImageDate: string;
    isFetching?: boolean;
}

const defaultFilter = () => true;
const defaultData: unknown[] = [];
function sortDaysWithImages(days: ImageDayCount[]): ImageDayCount[] {
    return [...days].sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
}

export function useImageState<T>({
    data = defaultData as T[],
    filter = defaultFilter,
    mapper,
    loading,
    queryParam,
    timezone = 'UTC',
    dayswithimages: externalDays,
    onDayChange,
    isFetching = false,
}: ImageInput<T>): ImageState {
    const usesExternalDays = externalDays !== undefined;
    // The image times are worded in the page language, so the list is rebuilt on a switch.
    const { i18n } = useTranslation();
    const language = i18n.resolvedLanguage ?? i18n.language;

    const images = useMemo(() => {
        if (!Array.isArray(data)) {
            return [];
        }
        return data.flatMap((row) => {
            // Runtime boundary: skip non-objects so a bad API element cannot throw in mapper.
            if (row == null || typeof row !== 'object') {
                return [];
            }
            try {
                const mapped = mapper(row as T);
                // A mapper returns null to drop a row deliberately.
                if (mapped == null || typeof mapped !== 'object') {
                    return [];
                }
                const image = withTimezoneMetadata(mapped, timezone, queryParam, language);
                return filter(image) ? [image] : [];
            } catch (error) {
                /* Kept as a boundary against a malformed API row, but no longer silent: a
                   mapper that throws used to make the image vanish from the list with no
                   trace anywhere, which is how a missing optional-chain in one mapper hid a
                   bug where non-admins saw fewer images than admins. */
                console.error('Skipping an image the mapper could not handle', error);
                return [];
            }
        });
    }, [data, mapper, timezone, queryParam, filter, language]);

    const dates = useMemo(
        () =>
            images.reduce(
                (acc, image) => {
                    if (acc[image.day] === undefined) {
                        acc[image.day] = {
                            count: 0,
                            day: image.day,
                        };
                    }
                    acc[image.day].count++;
                    return acc;
                },
                {} as Record<string, ImageDayCount>
            ),
        [images]
    );
    const dayswithimages = useMemo(
        () =>
            usesExternalDays
                ? sortDaysWithImages(externalDays)
                : sortDaysWithImages(Object.values(dates)),
        [usesExternalDays, externalDays, dates]
    );

    const [queryParams] = useQueryParams();
    const imageId = queryParamToImageId(queryParams, queryParam);
    const initialImage = useMemo(() => {
        const image = images?.find((image) => image.id === imageId);
        return image === undefined ? images[0] : image;
    }, [images, imageId, images[0], queryParam]);

    const initialImageDate = initialImage?.day ?? '';

    const [selectedImageDate, setSelectedImageDate] = useState<string>(initialImageDate);

    // Reset the chosen day when the set of images changes, not when the same images are only
    // re-worded for another page language: the list is rebuilt on a language switch.
    const imageSource = useMemo(
        () => ({ data, mapper, timezone, queryParam, filter }),
        [data, mapper, timezone, queryParam, filter]
    );
    const [prevSource, setPrevSource] = useState(imageSource);
    const [prevHadImages, setPrevHadImages] = useState(images.length > 0);
    const [prevImageId, setPrevImageId] = useState<string | undefined>(imageId);
    const sourceChanged = prevSource !== imageSource && (prevHadImages || images.length > 0);
    if (sourceChanged || prevImageId !== imageId) {
        setSelectedImageDate(initialImageDate);
        setPrevSource(imageSource);
        setPrevHadImages(images.length > 0);
        setPrevImageId(imageId);
    }

    const imagesForSelectedDate = useMemo(() => {
        if (usesExternalDays) {
            return images;
        }
        return images.filter((image) => toDateString(image.day) === selectedImageDate);
    }, [images, selectedImageDate, usesExternalDays]);

    const initialImageIndex = useMemo(() => {
        const index = imagesForSelectedDate.findIndex((image) => image.id === imageId);
        if (index === -1) return 0;
        return index;
    }, [imagesForSelectedDate, imageId]);

    return {
        images: imagesForSelectedDate,
        dayswithimages,
        onAfterTimelineChangeHandler: onDayChange ?? setSelectedImageDate,
        initialImageIndex,
        loading: loading,
        selectedImageDate,
        isFetching,
    };
}
